// Keeps shift-detail actions out of the already-large shift-list module.

import { Clock, Eye, Plus, RefreshCw } from 'lucide-react';
import React from 'react';

import { ShiftNotesModalButton } from '@apps/web/components/outreaches/shift_notes_modal';
import { Button } from '@apps/web/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@apps/web/components/ui/dropdown_menu';
import { Spinner } from '@apps/web/components/ui/spinner';
import { type Outreach, type Shift } from '@libraries/typescript/api_client';

type ShiftDetailsHeaderActionsProps = {
    readonly shift: Shift;
    readonly organizationId: string | null | undefined;
    readonly readOnly: boolean;
    readonly hasActiveCallout: boolean;
    readonly activeCallout: Outreach | null;
    readonly activeClockReminder: Outreach | null;
    readonly hasSyncEnabledAccountForShift: boolean;
    readonly isSyncingSelectedShift: boolean;
    readonly onSyncSelectedShift: () => void;
    readonly onCreateCallout: (shift: Shift) => void;
    readonly onNavigateToOutreach: (outreachId: string) => void;
};

export function ShiftDetailsHeaderActions({
    shift,
    organizationId,
    readOnly,
    hasActiveCallout,
    activeCallout,
    activeClockReminder,
    hasSyncEnabledAccountForShift,
    isSyncingSelectedShift,
    onSyncSelectedShift,
    onCreateCallout,
    onNavigateToOutreach,
}: ShiftDetailsHeaderActionsProps) {
    if (!organizationId && readOnly) {
        return null;
    }

    return (
        <div className="flex items-center gap-2">
            {organizationId ? (
                <ShiftNotesModalButton
                    organizationId={organizationId}
                    shift={shift}
                />
            ) : null}
            {!readOnly ? (
                <React.Fragment>
                    {hasSyncEnabledAccountForShift &&
                    shift.status !== 'missing' ? (
                        <Button
                            variant="outline"
                            onClick={onSyncSelectedShift}
                            disabled={isSyncingSelectedShift}
                            className="cursor-pointer"
                        >
                            {isSyncingSelectedShift ? (
                                <React.Fragment>
                                    <Spinner className="mr-2" />
                                    Syncing…
                                </React.Fragment>
                            ) : (
                                <React.Fragment>
                                    <RefreshCw className="mr-2 h-4 w-4" />
                                    Sync
                                </React.Fragment>
                            )}
                        </Button>
                    ) : null}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild={true}>
                            <Button variant="outline" className="gap-2">
                                Actions
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            {hasActiveCallout && activeCallout ? (
                                <DropdownMenuItem
                                    onClick={() =>
                                        onNavigateToOutreach(activeCallout.id)
                                    }
                                >
                                    <Eye className="mr-2 h-4 w-4" />
                                    View Outreach
                                </DropdownMenuItem>
                            ) : (
                                <DropdownMenuItem
                                    onClick={() => onCreateCallout(shift)}
                                    disabled={
                                        new Date(shift.shift_end_time) <
                                        new Date()
                                    }
                                >
                                    <Plus className="mr-2 h-4 w-4" />
                                    Find Caregivers
                                </DropdownMenuItem>
                            )}
                            {activeClockReminder &&
                            (shift.latest_stage === 'clock_in_reminder' ||
                                shift.latest_stage === 'clock_out_reminder') ? (
                                <DropdownMenuItem
                                    onClick={() =>
                                        onNavigateToOutreach(
                                            activeClockReminder.id,
                                        )
                                    }
                                >
                                    <Clock className="mr-2 h-4 w-4" />
                                    View Clock Reminder
                                </DropdownMenuItem>
                            ) : null}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </React.Fragment>
            ) : null}
        </div>
    );
}
